import {
	Backdrop,
	Box,
	ButtonBase,
	CircularProgress,
	Paper,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors } from "../styles/colors";
import { Icon } from "./Icon";
import { Checkbox } from "./Checkbox";

const WIDTH = "263px";

export const OfferDropdown = ({
	selectedOffers,
	offers,
	onAddOffer,
	onRemoveOffer,
	containerStyle,
	loading,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [heightDropdown, setHeightDropdown] = useState(0);

	const containerRef = useRef();
	const dropdownRef = useRef();

	const maxAmountSelectedOffers = useMemo(
		() => selectedOffers.length === 2,
		[selectedOffers],
	);

	const [position, setPosition] = useState({ x: 0, y: 0 });

	const updatePosition = () => {
		const rect = containerRef.current.getBoundingClientRect();
		const containerMidpoint = rect.top + rect.height / 2;
		const viewportMidpoint = window.innerHeight / 2;

		const yOffset =
			containerMidpoint > viewportMidpoint
				? rect.top - heightDropdown - 4
				: rect.bottom + 4;

		setPosition({
			x: rect.left,
			y: yOffset,
		});
	};

	useEffect(() => {
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		updatePosition();

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, []);

	useEffect(() => {
		// Get the height of the dropdown once it is mounted
		if (dropdownRef.current) {
			setHeightDropdown(dropdownRef.current.clientHeight);
		}
	}, []);

	return (
		<Box ref={containerRef} sx={{ position: "relative", ...containerStyle }}>
			<Box
				boxShadow="0 4px 20px 0 rgba(0, 0, 0, 0.05)"
				sx={{ borderRadius: "20px" }}
			>
				<ButtonBase
					sx={{
						borderRadius: "20px",
						backgroundColor: "white.main",
						height: "39px",
						pl: "16px",
						pr: "20px",
						width: WIDTH,
						border: "1px solid #EFF0F6",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
					onClick={() => setIsOpen((prev) => !prev)}
					disabled={loading}
				>
					<Typography
						sx={{
							fontFamily: "Inter",
							fontWeight: "500",
							fontSize: 16,
							lineHeight: "39px",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>{`Offer: ${selectedOffers.length === 0 ? "" : selectedOffers.length === 1 ? selectedOffers[0].name : "Custom"} `}</Typography>

					<Box
						sx={{
							position: "absolute",
							right: "10px",
							display: "flex",
							alignItems: "center",
						}}
					>
						{loading ? (
							<CircularProgress size={20} />
						) : (
							<Icon name={"arrowDownSmall"} />
						)}
					</Box>
				</ButtonBase>
			</Box>
			<Backdrop
				sx={{
					color: colors.white,
					zIndex: 10,
					backgroundColor: colors.transparent,
				}}
				open={isOpen}
				onClick={() => setIsOpen(false)}
			>
				<Box
					ref={dropdownRef}
					sx={{
						width: WIDTH,
						position: "absolute",
						top: position.y,
						left: "248px",
						borderRadius: "20px",
						overflow: "hidden",
						boxShadow: "0 2px 24px 0 rgba(0, 0, 0, 0.1)",
						ml: "39px",
					}}
				>
					<Paper
						sx={{
							"backgroundColor": "white.main",
							"maxHeight": "300px",
							"py": "12px",
							"overflow": "auto",
							"scrollbarColor": `${colors.primary} transparent`, // For Firefox
							"scrollbarWidth": "thin", // For Firefox
							"&::-webkit-scrollbar": {
								width: "4px",
							},
							"&::-webkit-scrollbar-thumb": {
								backgroundColor: colors.primary,
							},
							"&::-webkit-scrollbar-track": {
								backgroundColor: "transparent",
							},
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{offers.map((offer) => {
							const selected = selectedOffers.some(
								(selectedOffer) => selectedOffer.id === offer.id,
							);
							const onChange = selected ? onRemoveOffer : onAddOffer;
							const disabled = maxAmountSelectedOffers
								? selected
									? false
									: true
								: false;

							return (
								<Box
									sx={{
										"display": "flex",
										"alignItems": "center",
										"py": "8px",
										"px": "21px",
										"&:hover": {
											cursor: "pointer",
										},
									}}
									onClick={() => {
										if (!disabled) {
											onChange(offer);
										}
									}}
									key={`offer-dropdown-${offer.id}`}
								>
									<Checkbox
										name={offer.name}
										disableRipple
										disabled={disabled}
										textStyle={{
											fontSize: "12px",
											lineHeight: "14px",
											color: "#2C2C2C",
										}}
										checked={selected}
									/>
								</Box>
							);
						})}
					</Paper>
				</Box>
			</Backdrop>
		</Box>
	);
};
